import React from "react";
import { UserAvatar } from "./DisplayImage";
import {
  getColorFromStringHash,
  getNameInitials,
} from "../../utils/helperFunctions";
import useTheme from '../../hooks/useTheme';

/**
 * Square avatar for use inside grid cells (no border radius)
 */
const GridCellAvatar = ({ url, name, size }) => {
  const theme = useTheme();
  const nameInitials = getNameInitials(name);
  const bgColor = getColorFromStringHash(name);

  if (!url || url.length < 1) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: bgColor,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: `${size * 0.35}px`,
            color: theme.colors.white,
            fontWeight: 500,
          }}
        >
          {nameInitials}
        </span>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={name || "User"}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        backgroundColor: theme.colors.lightGrey,
      }}
    />
  );
};


export const MultiUserAvatarGrid = ({ profiles = [], size = 50 }) => {
  if (!profiles || profiles.length === 0) {
    return (
      <UserAvatar url="" style={{ height: `${size}px`, width: `${size}px` }} />
    );
  }

  if (profiles.length === 1) {
    const profile = profiles[0];
    return (
      <UserAvatar
        key={profile.userId}
        url={profile.profileImageUrl}
        name={profile.name}
        style={{ height: `${size}px`, width: `${size}px` }}
      />
    );
  }

  const halfSize = size / 2;
  const displayProfiles = profiles.slice(0, 4);

  if (profiles.length === 2) {
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          minHeight: `${size}px`,
          display: "flex",
          flexDirection: "row",
          borderRadius: "50%",
          overflow: "hidden",
          flexShrink: 0,
          aspectRatio: "1",
        }}
      >
        {displayProfiles.map((profile, index) => (
          <div
            key={profile.userId || index}
            style={{
              width: `${halfSize}px`,
              height: `${size}px`,
              overflow: "hidden",
            }}
          >
            <GridCellAvatar
              url={profile.profileImageUrl}
              name={profile.name}
              size={halfSize}
            />
          </div>
        ))}
      </div>
    );
  }

  // 3-4 users: 2x2 grid
  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        borderRadius: "50%",
        overflow: "hidden",
        flexShrink: 0,
        aspectRatio: "1",
      }}
    >
      {[0, 1, 2, 3].map((index) => {
        const profile = displayProfiles[index];
        return (
          <div
            key={profile?.userId || index}
            style={{
              width: `${halfSize}px`,
              height: `${halfSize}px`,
              overflow: "hidden",
            }}
          >
            <GridCellAvatar
              url={profile?.profileImageUrl || ""}
              name={profile?.name || ""}
              size={halfSize}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * Returns a display name for multiple chat participants
 * - 1 user: "John"
 * - 2 users: "John and Jane"
 * - 3+ users: "John and 2 others"
 */
export const getMultiUserDisplayName = (profiles, t) => {
  if (!profiles || profiles.length === 0) {
    return t ? t("chat.unknown_user") : "Unknown";
  }

  const firstName = profiles[0]?.name || "";

  if (profiles.length === 1) {
    return firstName;
  }

  if (profiles.length === 2) {
    const secondName = profiles[1]?.name || "";
    return `${firstName} ${t ? t("and") : "and"} ${secondName}`;
  }

  const othersCount = profiles.length - 1;
  return `${firstName} ${t ? t("and") : "and"} ${othersCount} ${t ? t("chat.others") : "others"}`;
};
